/**
 * Helpers to hand-off state between two chat components (e.g. NewChatInput -> Chat)
 */

import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {ChatFileUpload} from './ChatLibraryTypes';

export interface InitialChatData {
  /* The initial message to the chat (i.e. client-side shape of a DisplayMessageChatEvent). */
  message: {
    text: string;
    fileUploads: Array<ChatFileUpload>;
    agentType: AgentType;
  };
  /**
   * Promise that resolves when the initial message has been posted to the backend. It returns the ID of the
   * DisplayMessageChatEvent that was created.
   *
   * If this rejects with a 4xx error, the chat can continue but the user's message should be ignored.
   *
   * If this rejects with a 5xx error, additional work must be done to confirm if the message was posted or
   * it crashed afterwards. It may be simpler to let this simply error without explicitly handling.
   */
  messagePromise: Promise<{id: string}>;
}

/* Keyed by ChatSession.id */
export const InitialChatDataGlobalClientState = makeUseGlobalClientState<InitialChatData | null>(null);

/**
 * This should be invoked before a chat component is rendered, as chat components may use
 * `getInitialChatData` without a redux subscription.
 *
 * `data` should only transition to-or-from `null`, never between two non-null values, as chat components
 * are not expected to handle this case.
 */
export function setInitialChatData(chatSessionId: string, data: InitialChatData | null): void {
  InitialChatDataGlobalClientState.updateState(chatSessionId, data);
}

/**
 * This is safe to access directly in render, to populate initial state / when chatSessionId changes.
 */
export function getInitialChatData(chatSessionId: string): InitialChatData | null {
  return InitialChatDataGlobalClientState.getState(chatSessionId);
}
